import React, { useEffect, useMemo, useState } from 'react';
import { pickBy, startCase, head, capitalize, get } from 'lodash';
import Pagination from 'partial/components/Pagination';
import SearchBar from 'partial/components/input/SearchBar';
import ThumbIcon from 'partial/components/ThumbIcon';
import { BsCircleFill } from 'react-icons/bs';
import classNames from 'classnames';
import EmptyPage from 'partial/components/EmptyPage';

import List from 'partial/components/List';
import { usePersistState } from 'helper';
import {
  Navigate,
  Route,
  Routes,
  useNavigate,
  useParams,
} from 'react-router-dom';
import { useModuleContext } from 'partial/wrappers/ModuleWrapper';
import { useUpgradeRequestList } from './hooks';
import RequestDetails from './components/RequestDetails';

const INITIAL_STATE = {
  keyword: '',
  status: 'PENDING',
};

export function UpgradeRequest() {
  const { setSubLabel } = useModuleContext();
  const params = useParams();
  const id = get(params, '*');
  const [filter, setFilter] = useState(INITIAL_STATE);
  const [deleted, setDeleted] = useState(false);
  const [request, setRequest] = useState('');
  const [page, setPage] = useState({ page: '' });

  const payload = useMemo(
    () =>
      pickBy({
        ...filter,
        ...page,
        per_page: 15,
      }),
    [filter, page],
  );
  const [isLoading, list, meta, isRefetching] = useUpgradeRequestList(payload);

  const [selected, setSelected] = usePersistState('upgrade-request-id', '', {
    keepOnUnmount: false,
  });

  useEffect(() => {
    if (!id) return;
    setSelected(id);
  }, [id, setSelected]);

  const navigate = useNavigate();

  const onSubSelect = (item) => {
    setDeleted(false);
    setSelected(item?.uuid);
    setRequest(item);
    navigate(`${item?.uuid}`);
  };

  useEffect(() => {
    if ((selected && !isLoading) || deleted || id) return;
    setSelected(head(list)?.uuid);
    setRequest(head(list));
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [isLoading, selected, setSelected, deleted]);

  useEffect(() => {
    setPage({ page: '' });
  }, [filter]);

  const match = useMemo(
    () => (list || [])?.find((x) => x?.uuid === selected),
    [list, selected],
  );

  useEffect(() => {
    if ((!selected && !list) || deleted) return;
    if (match) setRequest(match);
  }, [isRefetching, selected, list, match, deleted]);

  useEffect(() => {
    if (!meta) return;
    setSubLabel(
      meta?.total === 0
        ? ''
        : `Total of ${meta?.total} upgrade request${
            meta?.total === 1 ? '' : 's'
          }`,
    );
    return () => setSubLabel('');
  }, [meta, setSubLabel]);

  const callBack = () => {
    setSelected('');
    setRequest(null);
    setDeleted(true);
    navigate('', { replace: true });
  };

  return (
    <section className="min-h-[calc(100vh_-_5rem)] flex p-6 bg-white drop-shadow rounded-lg flex-grow static">
      <div className="pr-4 border-r border-gray-50 min-w-[300px] flex flex-col">
        <SearchBar
          name="keyword"
          value={filter.keyword}
          onChange={setFilter}
          inputClassName="py-[1.05rem] !w-full"
          containerClassName="mb-4"
        />
        <List
          data={list}
          isLoading={isLoading}
          selectedId={selected}
          onSelectRow={onSubSelect}
          containerClassName="!w-full"
          render={(item) => {
            const name = startCase(capitalize(item?.organization_name));
            return (
              <div className="flex justify-between items-center">
                <div className="flex items-center gap-2 pl-2 py-1 w-full">
                  <ThumbIcon className="h-8 w-8 rounded-full" alt={name} />
                  <div>
                    <div className="text-sm">{name}</div>
                    <div
                      className={classNames(
                        'text-xs font-semibold',
                        item?.status === 'APPROVED'
                          ? 'text-success-400'
                          : item?.status === 'REJECTED'
                          ? 'text-danger-400'
                          : 'text-inherit',
                      )}
                    >
                      {capitalize(item?.status)}
                    </div>
                  </div>
                </div>
                <div
                  className={classNames(
                    'transition',
                    item?.status === 'PENDING' && item?.uuid !== selected
                      ? 'opacity-100'
                      : 'opacity-0',
                  )}
                >
                  <BsCircleFill className="w-3 h-3 text-danger" />
                </div>
              </div>
            );
          }}
          selectedKey="uuid"
        />
        <div className="self-center mt-8">
          <Pagination value={meta} onChange={setPage} withPage={false} />
        </div>
      </div>
      <Routes>
        {match && (
          <Route
            path=":id/*"
            element={<RequestDetails data={request} callBack={callBack} />}
          />
        )}
        {list?.length > 0 && !deleted && (
          <Route
            index
            element={<Navigate replace to={`${head(list)?.uuid}`} />}
          />
        )}
        <Route
          path="*"
          element={
            <div className="flex-1">
              <EmptyPage />
            </div>
          }
        />
      </Routes>
    </section>
  );
}

export default UpgradeRequest;
